import type { ReactNode } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import ErrorBoundary from '@baaligames/shared-ui/components/ErrorBoundary';
import { AdminAuthProvider, useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { AdminLangProvider } from '@baaligames/shared-ui/admin/components/AdminLangContext';
import AdminAppShell from '@baaligames/shared-ui/admin/components/AdminAppShellV2';
import InstallPrompt from '@baaligames/shared-ui/lib/install';
import AdminLoginPage from './pages/AdminLogin';
// Account hub
import AccountHub from './pages/AccountHub';
import ProfilePanel from './panels/account/ProfilePanel';
import AppIconsPanel from './panels/account/AppIconsPanel';
// Routing Restructure (2026-05-16): AdminMore → AdminSettings
import AdminSettings from './pages/AdminSettings';
import GameDevSecurity from './pages/GameDevSecurity';
// Routing Restructure (2026-05-16): RewardsHub → OffersHub.
// /rewards/* legacy URLs redirect into /offers/*. The hub now hosts
// Offers + Claims + Bulk Pay + Referrals — every player payout in
// one place.
import OffersHub from './pages/OffersHub';
import OffersListPanel from './panels/rewards/OffersListPanel';
import ClaimsQueuePanel from './panels/rewards/ClaimsQueuePanel';
import AdminOfferDetail from './pages/AdminOfferDetail';
import AdminDashboard from './pages/AdminHome';
import AdminDepositLedger from './pages/AdminDepositLedger';
// 2026-05-16 (OperationsHub merge — Path C):
// OperationsHub was deleted; its 6 panels now drill into the Dashboard
// via `?drill=…` URL state (mounted inline in AdminHome.tsx). The
// panel files themselves still exist and are imported by AdminHome.
// App.tsx no longer mounts them as routes — every legacy /operations/*
// URL redirects into the new dashboard-drill state.
//
// Settings-hosted tools (Replay / Reassign / Level Rates) remain
// routed here as they were before — they're tools, not monitors,
// and live at their own /settings/* URLs.
import ReplayPanel from './panels/operations/ReplayPanel';
import ReassignPanel from './panels/operations/ReassignPanel';
import RebatePanel from './panels/operations/RebatePanel';
import PlatformLimitsPanel from './panels/operations/PlatformLimitsPanel';
import MasterCasinoProfitPanel from './panels/operations/MasterCasinoProfitPanel';
import ReservePanel from './panels/operations/ReservePanel';
import MatkaPanel from './panels/matka/MatkaPanel';
import UnassignedPlayersPanel from './panels/operations/UnassignedPlayersPanel';
// 2026-05-18 — Identity rollout soft-gate for admins too.
import CompleteProfileModal from './components/CompleteProfileModal';
// 2026-05-17 (Phase 7.8 follow-up): full-page admin ledger audit UI.
import AdminLedgerPanel from './panels/operations/AdminLedgerPanel';
// Games hub now lives under /settings/games/*
import GamesHub from './pages/GamesHub';
import GamesListPanel from './panels/games/GamesListPanel';
import TargetProfitReport from './pages/TargetProfitReport';
import TargetProfitSimulator from './pages/TargetProfitSimulator';
import AdminGameMoneyHub from './pages/AdminGameMoneyHub';
import BotsHub from './pages/BotsHub';
import GameConfigPanel from './panels/games/GameConfigPanel';
// Player / Agent hubs
import PlayersHub from './pages/PlayersHub';
import AgentsHub from './pages/AgentsHub';
import PlayerRequestsPanel from './panels/people/PlayerRequestsPanel';
import AgentRequestsPanel from './panels/people/AgentRequestsPanel';
import PlayersListPanel from './panels/people/PlayersListPanel';
import AgentsListPanel from './panels/people/AgentsListPanel';
// BotsListPanel is the bot ROSTER (CRUD). It now lives inside BotsHub at
// /people/bots (UX Phase 4) — App.tsx imports BotsHub, not the panel directly.
// Player-payout panels — now sub-tabs of /offers/*.
import BulkPayoutsPanel from './panels/finance/BulkPayoutsPanel';
import ReferralsPanel from './panels/finance/ReferralsPanel';
import AdminUserDetail from './pages/AdminUserDetail';
import AdminBotDetail from './pages/AdminBotDetail';
import AdminAgentDetail from './pages/AdminAgentDetail';
import AdminCreatePlayer from './pages/AdminCreatePlayer';

function AdminOnly({ children }: { children: ReactNode }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return <div className="p-10 text-center text-arena-500 dark:text-neon-200/55">Loading…</div>;
  if (!admin) return <Navigate to="/login" replace />;
  return children;
}

function ShellRoute({ element }: { element: JSX.Element }) {
  // 2026-05-18 — Mount CompleteProfileModal alongside the shell so it
  // self-renders on every authenticated page whenever first_name /
  // last_name are missing (birthdate too, but admins don't carry one).
  return (
    <AdminOnly>
      <AdminAppShell>{element}</AdminAppShell>
      <CompleteProfileModal />
    </AdminOnly>
  );
}

// Routing Restructure (2026-05-16): legacy root-level /:gameId now
// redirects to /settings/games/:gameId (was /games/:gameId).
function LegacyGameRedirect() {
  const { gameId } = useParams<{ gameId: string }>();
  return <Navigate to={`/settings/games/${gameId}`} replace />;
}

// <Navigate to="/people/agents/:id"> does NOT substitute :id — it
// sends the user to a literal URL containing ":id". This wrapper
// substitutes any `:name` segment in the `to` template with the
// matching value from useParams() before navigating.
function SubstNavigate({ to }: { to: string }) {
  const params = useParams();
  let target = to;
  for (const [k, v] of Object.entries(params)) {
    if (typeof v === 'string') {
      target = target.split(`:${k}`).join(v);
    }
  }
  return <Navigate to={target} replace />;
}

export default function App() {
  return (
    <AdminAuthProvider>
      <AdminLangProvider>
        <ErrorBoundary><Routes>
          <Route path="/login" element={<AdminLoginPage />} />

          {/* Dashboard */}
          <Route path="/" element={<ShellRoute element={<AdminDashboard />} />} />

          {/* Legacy bookmark: standalone deposit ledger URL. The
              deposit ledger is now a tab inside AgentDetail; the
              legacy URL keeps working. */}
          <Route path="/agents/:id/deposit" element={<ShellRoute element={<AdminDepositLedger />} />} />

          {/* ════════════════════════════════════════════════════════
              OPERATIONS HUB — DEPRECATED 2026-05-16 (Path C merge).
              The hub page and its 6 nested routes are gone. Every
              legacy /operations/* URL now redirects to the dashboard
              with the appropriate `?drill=<key>` query param, which
              mounts the same Operations panel inline. Bookmarks +
              external links continue to work.
             ════════════════════════════════════════════════════════ */}
          <Route path="/operations"               element={<Navigate to="/?drill=platform"     replace />} />
          <Route path="/operations/platform"      element={<Navigate to="/?drill=platform"     replace />} />
          <Route path="/operations/leaderboards"  element={<Navigate to="/?drill=leaderboards" replace />} />
          <Route path="/operations/activity"      element={<Navigate to="/?drill=activity"     replace />} />
          <Route path="/operations/bots"          element={<Navigate to="/?drill=bots"         replace />} />
          <Route path="/operations/audit"         element={<Navigate to="/?drill=audit"        replace />} />
          <Route path="/operations/dlq"           element={<Navigate to="/?drill=dlq"          replace />} />

          {/* Pre-Phase-12.5 deep-links to the same panels — also
              re-routed at the dashboard drill state. */}
          <Route path="/platform-dashboard"  element={<Navigate to="/?drill=platform"     replace />} />
          <Route path="/leaderboards"        element={<Navigate to="/?drill=leaderboards" replace />} />
          <Route path="/activity-feed"       element={<Navigate to="/?drill=activity"     replace />} />
          <Route path="/bot-matrix"          element={<Navigate to="/?drill=bots"         replace />} />
          <Route path="/audit/rate-changes"  element={<Navigate to="/?drill=audit"        replace />} />
          <Route path="/settlement/dlq"      element={<Navigate to="/?drill=dlq"          replace />} />

          {/* Operations tools that MOVED → /settings (kept from prior restructure) */}
          <Route path="/operations/replay"     element={<Navigate to="/settings/replay" replace />} />
          <Route path="/operations/reassign"   element={<Navigate to="/settings/reassign" replace />} />
          <Route path="/operations/level-rates" element={<Navigate to="/people/agents" replace />} />
          {/* The pre-Phase-12.5 aliases for the tools also re-point at /settings */}
          <Route path="/settlement/replay"   element={<Navigate to="/settings/replay" replace />} />
          <Route path="/bulk-reassign"       element={<Navigate to="/settings/reassign" replace />} />

          {/* ════════════════════════════════════════════════════════
              PEOPLE HUBS — Players (2 tabs) + Agents (2 tabs).
              Bulk + Referrals moved out to /offers/*; Bots moved to
              /settings/*.
             ════════════════════════════════════════════════════════ */}

          {/* PlayersHub — requests + list */}
          <Route path="/people/players" element={<ShellRoute element={<PlayersHub />} />}>
            <Route index               element={<Navigate to="requests" replace />} />
            <Route path="requests"     element={<PlayerRequestsPanel />} />
            <Route path="list"         element={<PlayersListPanel />} />
          </Route>

          {/* AgentsHub — requests + list */}
          <Route path="/people/agents" element={<ShellRoute element={<AgentsHub />} />}>
            <Route index               element={<Navigate to="requests" replace />} />
            <Route path="requests"     element={<AgentRequestsPanel />} />
            <Route path="list"         element={<AgentsListPanel />} />
          </Route>

          {/* /people root → land on Players */}
          <Route path="/people"        element={<Navigate to="/people/players" replace />} />

          {/* Detail / workflow routes (sibling pages, not tabs) */}
          <Route path="/people/players/:id"            element={<ShellRoute element={<AdminUserDetail />} />} />
          <Route path="/people/agents/:id"             element={<ShellRoute element={<AdminAgentDetail />} />} />
          <Route path="/people/agents/:id/players/new"        element={<ShellRoute element={<AdminCreatePlayer />} />} />
          <Route path="/people/players/new"                   element={<ShellRoute element={<AdminCreatePlayer />} />} />
          {/* UX Phase 4 (2026-08-08): the AdminRechargePlayer full page is retired.
              It duplicated the DirectWalletAdjustModal (same endpoint), had NO live
              UI entry point (only reachable by URL), and the modal already offers a
              platform-mint vs agent-pool source picker. These paths now redirect to
              the entity's detail page, where the ONE money modal opens from the kebab. */}
          <Route path="/people/agents/:id/recharge-player"          element={<SubstNavigate to="/people/agents/:id" />} />
          <Route path="/people/agents/:id/players/:userId/recharge" element={<SubstNavigate to="/people/players/:userId" />} />

          {/* Player-payout sub-tabs that MOVED → /offers */}
          <Route path="/people/players/bulk"      element={<Navigate to="/offers/bulk-pay" replace />} />
          <Route path="/people/players/referrals" element={<Navigate to="/offers/referrals" replace />} />

          {/* Bots — UX Phase 4: bots are people the admin manages, so the roster
              now lives in the People area at /people/bots (with the People segment
              bar). /settings/bots* redirects here (see below). */}
          <Route path="/people/bots"       element={<ShellRoute element={<BotsHub />} />} />
          <Route path="/people/bots/:id"   element={<ShellRoute element={<AdminBotDetail />} />} />

          {/* Legacy redirects → People hub paths (pre-Phase-3) */}
          <Route path="/users"               element={<Navigate to="/people/players" replace />} />
          <Route path="/users/:id"           element={<SubstNavigate to="/people/players/:id" />} />
          <Route path="/agents"              element={<Navigate to="/people/agents" replace />} />
          <Route path="/agents/:id"          element={<SubstNavigate to="/people/agents/:id" />} />
          <Route path="/agents/:id/players"  element={<SubstNavigate to="/people/agents/:id" />} />
          <Route path="/agents/:id/players/new"        element={<SubstNavigate to="/people/agents/:id/players/new" />} />
          {/* recharge-player page retired (UX Phase 4) — redirect straight to the
              entity detail, where the one money modal lives. */}
          <Route path="/agents/:id/recharge-player"    element={<SubstNavigate to="/people/agents/:id" />} />
          <Route path="/agents/:id/players/:userId/recharge" element={<SubstNavigate to="/people/players/:userId" />} />

          {/* Legacy /finance/* sub-paths removed 2026-08-08 (UX Phase 8 router
              hygiene): dismantled back in Phase 3, ZERO hard-loads across 7 days
              of nginx logs, no in-app refs. The bare /finance HEAD is kept so a
              muscle-memory hit lands on the requests queue — and isn't swallowed
              by the /:gameId legacy-game shim below (which would send single-word
              paths to the games hub). Deep sub-path bookmarks fall through to the
              "*" catch-all → dashboard. */}
          <Route path="/finance" element={<Navigate to="/people/players/requests" replace />} />

          {/* ════════════════════════════════════════════════════════
              OFFERS HUB (renamed from Rewards Hub).
              Tabs: list · claims · bulk-pay · referrals.
              Detail pages are siblings; the four tab paths take
              precedence over `:id` via React Router exact-match.
             ════════════════════════════════════════════════════════ */}
          <Route path="/offers" element={<ShellRoute element={<OffersHub />} />}>
            <Route index             element={<Navigate to="list" replace />} />
            <Route path="list"       element={<OffersListPanel />} />
            <Route path="claims"     element={<ClaimsQueuePanel />} />
            <Route path="bulk-pay"   element={<BulkPayoutsPanel />} />
            <Route path="referrals"  element={<ReferralsPanel />} />
          </Route>
          {/* Offer create / edit — sibling pages */}
          <Route path="/offers/new"   element={<ShellRoute element={<AdminOfferDetail />} />} />
          <Route path="/offers/:id"   element={<ShellRoute element={<AdminOfferDetail />} />} />

          {/* Legacy /rewards/* → /offers/* */}
          <Route path="/rewards"                 element={<Navigate to="/offers" replace />} />
          <Route path="/rewards/offers"          element={<Navigate to="/offers/list" replace />} />
          <Route path="/rewards/claims"          element={<Navigate to="/offers/claims" replace />} />
          <Route path="/rewards/offers/new"      element={<Navigate to="/offers/new" replace />} />
          <Route path="/rewards/offers/:id"      element={<SubstNavigate to="/offers/:id" />} />

          {/* Legacy /money/* sub-paths removed 2026-08-08 (UX Phase 8 router
              hygiene): pre-Phase-3 corpses, ZERO hard-loads in 7 days of nginx
              logs, no in-app refs. The bare /money HEAD is kept so a muscle-memory
              hit lands on the requests queue (not the /:gameId games-hub shim).
              Deep sub-path bookmarks fall through to "*" → dashboard. */}
          <Route path="/money" element={<Navigate to="/people/players/requests" replace />} />

          {/* Top-level duplicates → canonical /offers, /people/players/requests, etc. */}
          <Route path="/payment-requests"        element={<Navigate to="/people/players/requests" replace />} />
          <Route path="/payment-settings"        element={<Navigate to="/people/players/requests" replace />} />
          <Route path="/offer-claims"            element={<Navigate to="/offers/claims" replace />} />
          <Route path="/referrals"               element={<Navigate to="/offers/referrals" replace />} />
          <Route path="/bulk-payouts"            element={<Navigate to="/offers/bulk-pay" replace />} />

          {/* Legacy /bots → /people/bots DIRECTLY (Phase 8 fix): /settings/bots is
              itself only a redirect to /people/bots, so the old /bots → /settings/bots
              → /people/bots double-hop chain is now a single hop. */}
          <Route path="/bots"             element={<Navigate to="/people/bots" replace />} />
          <Route path="/bots/:id"         element={<SubstNavigate to="/people/bots/:id" />} />

          {/* ════════════════════════════════════════════════════════
              SETTINGS HUB (renamed from More).
              A sectioned card grid + flat tool routes underneath:
                /settings              landing
                /settings/games        nested GamesHub
                /settings/replay       ReplayPanel
                /settings/reassign     ReassignPanel
                /settings/bots         BotsListPanel
                /settings/bots/:id     AdminBotDetail
             ════════════════════════════════════════════════════════ */}
          <Route path="/settings"             element={<ShellRoute element={<AdminSettings />} />} />
          {/* level-rates retired (rebate v3 cleanup): partnership %s live per-agent */}
          <Route path="/settings/level-rates" element={<Navigate to="/people/agents" replace />} />
          {/* commission page retired (2026-07-09): its switches + per-agent rates
              folded into the Rebate & platform fees page — one money page only */}
          <Route path="/settings/commission"  element={<Navigate to="/settings/rebate" replace />} />
          <Route path="/settings/rebate"      element={<ShellRoute element={<RebatePanel />} />} />
          <Route path="/settings/platform"    element={<ShellRoute element={<PlatformLimitsPanel />} />} />
          <Route path="/settings/casino-profit" element={<ShellRoute element={<MasterCasinoProfitPanel />} />} />
          {/* Phase 2 Stage 3.5 (2026-09-15): the bounded reserve card — HOW each game
              delivers the target set above it. It renders inside the Game money hub's
              Earnings tab (same family, one page); this is its deep link. */}
          <Route path="/settings/reserve"     element={<ShellRoute element={<ReservePanel />} />} />
          {/* Matka King operations (2026-09-19): markets, declare, engine book, bets, week. */}
          <Route path="/matka"                element={<ShellRoute element={<MatkaPanel />} />} />
          <Route path="/settings/matka"       element={<Navigate to="/matka" replace />} />
          {/* UX Phase 3 (2026-08-08): the Game Money hub — one tabbed home for
              the profit report, simulator, earnings control, economics + game
              availability. The individual routes above/below still work as deep
              links; this just gathers them under one roof. */}
          {/* UX Phase 4: Game Money promoted to a top-level nav destination.
              /settings/game-money stays as a deep-link alias so old links + the
              ?tab= deep-links keep working. */}
          <Route path="/game-money"          element={<ShellRoute element={<AdminGameMoneyHub />} />} />
          <Route path="/settings/game-money" element={<ShellRoute element={<AdminGameMoneyHub />} />} />
          <Route path="/settings/replay"      element={<ShellRoute element={<ReplayPanel />} />} />
          <Route path="/settings/reassign"    element={<ShellRoute element={<ReassignPanel />} />} />
          {/* Bots moved to People (UX Phase 4) — keep these redirecting for old links. */}
          <Route path="/settings/bots"        element={<Navigate to="/people/bots" replace />} />
          <Route path="/settings/bots/:id"    element={<SubstNavigate to="/people/bots/:id" />} />
          {/* 2026-05-17 (Phase 7.8 follow-up): admin wallet ledger audit page. */}
          <Route path="/settings/admin-ledger" element={<ShellRoute element={<AdminLedgerPanel />} />} />
          {/* Dark Launch sprint (2026-05-21): lock/unlock lobby cards +
              universal developer PIN management. Admin-only at the API
              layer too — see adminGames.routes.js. */}
          <Route path="/settings/game-dev-security" element={<ShellRoute element={<GameDevSecurity />} />} />
          {/* Legacy alias so /operations/admin-ledger also works. */}
          <Route path="/operations/admin-ledger" element={<Navigate to="/settings/admin-ledger" replace />} />

          {/* 2026-05-18 (Phase C3): admin-only maintenance page surfacing
              orphan players from before the architecture cleanup. */}
          <Route path="/settings/unassigned-players" element={<ShellRoute element={<UnassignedPlayersPanel />} />} />

          {/* /settings/games — nested GamesHub with per-game tabs */}
          <Route path="/settings/games" element={<ShellRoute element={<GamesHub />} />}>
            <Route index            element={<GamesListPanel />} />
            <Route path=":gameId"   element={<GameConfigPanel />} />
          </Route>

          {/* TP-6: Target-Profit 8-quarter daily report */}
          <Route path="/reports/target-profit" element={<ShellRoute element={<TargetProfitReport />} />} />
          {/* TP-5: Target-Profit predictive "adrenaline" simulator */}
          <Route path="/reports/target-profit-simulator" element={<ShellRoute element={<TargetProfitSimulator />} />} />

          {/* Legacy /more → /settings */}
          <Route path="/more"             element={<Navigate to="/settings" replace />} />

          {/* Account hub (unchanged) */}
          <Route path="/account" element={<ShellRoute element={<AccountHub />} />}>
            <Route index            element={<Navigate to="profile" replace />} />
            <Route path="profile"   element={<ProfilePanel />} />
            <Route path="app-icons" element={<AppIconsPanel />} />
          </Route>

          {/* Legacy redirects → Account hub */}
          <Route path="/profile"          element={<Navigate to="/account/profile" replace />} />
          <Route path="/app-icons"        element={<Navigate to="/account/app-icons" replace />} />
          {/* 2026-05-16: bare /me also redirects so the agent-only
              "My Pool" card on /settings lands on something useful
              when an agent is viewing the admin SPA (rather than
              falling through to the catch-all and bouncing to /). */}
          <Route path="/me"               element={<Navigate to="/account/profile" replace />} />
          <Route path="/me/wallet"        element={<Navigate to="/account/profile" replace />} />
          <Route path="/me/earnings"      element={<Navigate to="/account/profile" replace />} />

          {/* Legacy /games + /games/:gameId → /settings/games + /settings/games/:gameId */}
          <Route path="/games"            element={<Navigate to="/settings/games" replace />} />
          <Route path="/games/:gameId"    element={<SubstNavigate to="/settings/games/:gameId" />} />

          {/* Legacy /:gameId at root → /settings/games/:gameId.
              MUST come last since :gameId is a single-segment catch-all. */}
          <Route path="/:gameId" element={<LegacyGameRedirect />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes></ErrorBoundary>
        <InstallPrompt variant="admin" />
      </AdminLangProvider>
    </AdminAuthProvider>
  );
}
